import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { toast } from 'src/components/shared/snackbar'
import ManageAccounts from '../components/manageAccounts/ManageAccounts'
import {
  DEFAULT_CONTAINER_STATE,
  EXCHANGE_PROVIDER_PROCESS_STEP,
  QR_CODE_PROCESS_STEP
} from '../constants/containerStates'
import {
  changeExchangeProvidersState,
  connectExchangeProvider,
  resetExchangeProvider,
  updateExchangeProvider
} from '../store/actions/exchange'
import {
  exchangeProviderAccountLabel,
  extractMembershipNumber,
  filterExchangeProviderForManage
} from '../util/pointsExchange.helpers'
import { validationRulesForAccountNumberSanity } from '../util/validation/validation.helpers'

import CameraBlocked from '../components/popups/qrCode/CameraBlocked'
import ConnectAccountByQrCode from '../components/popups/qrCode/ConnectAccountByQrCode'
import { EXCHANGE_PROVIDER_PROFILE_DISPATCH } from '../store/reducers/exchange/exchangeProviders'
import {
  selectEarnViaProfile,
  selectExchangeProviderProfile,
  selectMainProfile
} from '../store/selectors/common'
import { containsNoValues } from '../util/object.helpers'
import { extractCodeOrInputText } from '../util/string.helpers'
import { checkValidations } from '../util/validation/commonValidation.helpers'
import {
  connectSocialAccount,
  getUsernameByTID
} from '../store/actions/earnVia'
import { CUSTOM_BRAND_ID } from '../constants/pointsExchange'

const ManageAccountsContainer = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const exchangeProviderProfile = useSelector(selectExchangeProviderProfile)
  const mainProfile = useSelector(selectMainProfile)
  const earnViaProfile = useSelector(selectEarnViaProfile)

  const [popupState, setPopupState] = useState(DEFAULT_CONTAINER_STATE.NONE)
  const [provider, setProvider] = useState(null)

  const connectedXAccount = extractMembershipNumber(
    mainProfile.exchangeMemberships,
    { brandId: CUSTOM_BRAND_ID.X }
  )

  useEffect(() => {
    dispatch(getUsernameByTID(connectedXAccount))
    return () => dispatch(resetExchangeProvider())
  }, [dispatch, connectedXAccount])

  useEffect(() => {
    if (exchangeProviderProfile?.accountSaveState) {
      handleAccountSaveState(exchangeProviderProfile.accountSaveState)
      setPopupState(exchangeProviderProfile.accountSaveState)
    }
  }, [exchangeProviderProfile?.accountSaveState])

  const exchangeWhitelist = useMemo(
    () => filterExchangeProviderForManage(mainProfile.exchangeWhitelist),
    [mainProfile.exchangeWhitelist]
  )

  const handleAccountSaveState = (state) => {
    if (!provider?.brandName) return

    const messages = {
      [EXCHANGE_PROVIDER_PROCESS_STEP.CONNECTED_ACCOUNT]: 'common.connected',
      [EXCHANGE_PROVIDER_PROCESS_STEP.CHANGED_ACCOUNT]: 'common.changed'
    }

    if (messages[state]) {
      toast.success(
        t('exchange.account-saved-success', {
          providerName: provider.brandName,
          label: t(messages[state])
        })
      )
    } else if (state === EXCHANGE_PROVIDER_PROCESS_STEP.WRONG_ACCOUNT_NUMBER) {
      wrongAccountNumberToast()
    }

    setProvider(null)
  }

  const wrongAccountNumberToast = () => {
    if (provider?.brandName) {
      toast.error(
        t('exchange.incorrect-account-number', {
          providerName: provider.brandName,
          fieldLabel: exchangeProviderAccountLabel(t, provider, true)
        })
      )
    }
  }

  const handleQrCodeClick = (provider) => {
    setProvider(provider)
    setPopupState(QR_CODE_PROCESS_STEP.SCAN_CODE)
  }

  const handleQrCodeError = () =>
    setPopupState(QR_CODE_PROCESS_STEP.CAMERA_BLOCKED)

  const handleQrCodeScan = (text) => {
    if (!text || exchangeProviderProfile?.isProcessing) return

    const accountNumber = extractCodeOrInputText(text)
    if (accountNumber)
      validateAndSaveAccountNumber(provider, accountNumber, true)
  }

  const validateAndSaveAccountNumber = useCallback(
    (provider, accountNumber, isQrScan = false) => {
      const errors = checkValidations({
        connectAccountNumber: {
          value: accountNumber,
          rules: validationRulesForAccountNumberSanity(provider.brandId)
        }
      })

      const membership = mainProfile.exchangeMemberships[provider.brandId]
      const isValid =
        containsNoValues(errors) &&
        membership?.membershipNumber !== accountNumber

      if (isValid) {
        membership
          ? dispatch(
              updateExchangeProvider(
                membership?.id,
                provider?.brandId,
                accountNumber
              )
            )
          : dispatch(connectExchangeProvider(provider.brandId, accountNumber))
      } else {
        isQrScan
          ? wrongAccountNumberToast()
          : dispatch(
              changeExchangeProvidersState(
                EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR,
                errors
              )
            )
      }
    },
    [dispatch, mainProfile.exchangeMemberships]
  )

  const resetError = useCallback(
    () =>
      dispatch(
        changeExchangeProvidersState(
          EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR,
          {}
        )
      ),
    [dispatch]
  )

  const handleSaveAccount = useCallback(
    (provider, value) => {
      setProvider(provider)
      resetError()
      if (provider.isOauthBrand) {
        dispatch(connectSocialAccount(provider.brandId))
      } else {
        validateAndSaveAccountNumber(provider, value)
      }
    },
    [resetError, validateAndSaveAccountNumber]
  )

  const handleNoPopup = () => setPopupState(DEFAULT_CONTAINER_STATE.NONE)

  const renderPopup = () => {
    if (popupState === QR_CODE_PROCESS_STEP.SCAN_CODE) {
      return (
        <ConnectAccountByQrCode
          onCancel={handleNoPopup}
          onError={handleQrCodeError}
          onScan={handleQrCodeScan}
          t={t}
        />
      )
    }
    if (popupState === QR_CODE_PROCESS_STEP.CAMERA_BLOCKED) {
      return <CameraBlocked onCancel={handleNoPopup} t={t} />
    }
    return null
  }

  return (
    <>
      <ManageAccounts
        exchangeWhitelist={exchangeWhitelist}
        memberships={mainProfile.exchangeMemberships}
        selectedProvider={provider}
        onSaveAccount={handleSaveAccount}
        onQrCodeClick={handleQrCodeClick}
        onChange={resetError}
        exchangeProviderProfile={exchangeProviderProfile}
        earnViaProfile={earnViaProfile}
        t={t}
      />
      {renderPopup()}
    </>
  )
}

export default ManageAccountsContainer
